import { textIncludesQuery } from '@narrativebench/editor-core';
import type {
  CreateableMentionableEntityType,
  MentionableEntity,
} from '../components/RichTextField';
import { getEntityIconSvg } from './concept-icons';

export const MENTION_SUGGESTION_LIMIT = 20;
export const MENTION_NO_MATCH_ID = 'mention-no-match';

export const MENTIONABLE_ENTITY_TYPES: CreateableMentionableEntityType[] = [
  'character',
  'location',
  'manual_chapter',
  'storyline',
  'scene',
  'resource',
  'timeline_item',
  'document',
];

export const MENTION_ADD_NEW_LABEL_KEYS: Record<CreateableMentionableEntityType, string> = {
  character: 'mention_add_new_character',
  location: 'mention_add_new_location',
  manual_chapter: 'mention_add_new_chapter',
  storyline: 'mention_add_new_storyline',
  scene: 'mention_add_new_scene',
  resource: 'mention_add_new_resource',
  timeline_item: 'mention_add_new_timeline_item',
  document: 'mention_add_new_document',
};

export type MentionNoMatchItem = {
  id: typeof MENTION_NO_MATCH_ID;
  label: string;
  type: 'no_match';
  suggestionKind: 'no_match';
};

export type MentionCreateItem = {
  id: string;
  label: string;
  type: CreateableMentionableEntityType;
  suggestionKind: 'create';
  query: string;
};

export type MentionSuggestionItem = MentionableEntity | MentionNoMatchItem | MentionCreateItem;

export function isMentionNoMatchItem(item: MentionSuggestionItem): item is MentionNoMatchItem {
  return 'suggestionKind' in item && item.suggestionKind === 'no_match';
}

export function isMentionCreateItem(item: MentionSuggestionItem): item is MentionCreateItem {
  return 'suggestionKind' in item && item.suggestionKind === 'create';
}

export function shouldShowMentionDocumentTitle(
  item: { label: string; documentId?: string; documentTitle?: string },
  currentDocumentId?: string | null,
): boolean {
  const documentTitle = item.documentTitle?.trim();
  if (!documentTitle) {
    return false;
  }
  if (item.label.includes(documentTitle)) {
    return false;
  }
  if (currentDocumentId && item.documentId && item.documentId === currentDocumentId) {
    return false;
  }
  return true;
}

export function filterMentionableEntities(
  entities: MentionableEntity[],
  query: string,
  limit = MENTION_SUGGESTION_LIMIT,
): MentionableEntity[] {
  const q = query.trim();
  const qNum = /^\d+$/.test(q) ? parseInt(q, 10) : null;
  const chapterByOrder =
    qNum !== null
      ? entities.filter((e) => e.type === 'manual_chapter' && Number(e.order) === qNum)
      : [];
  const sceneByOrder =
    qNum !== null
      ? entities.filter((e) => e.type === 'scene' && Number(e.order) === qNum)
      : [];
  const rest = entities.filter((e) => {
    if (qNum !== null && e.type === 'manual_chapter' && Number(e.order) === qNum) return false;
    if (qNum !== null && e.type === 'scene' && Number(e.order) === qNum) return false;
    return (
      textIncludesQuery(e.label ?? '', q) ||
      textIncludesQuery(e.id ?? '', q) ||
      textIncludesQuery(e.documentTitle ?? '', q)
    );
  });
  return [...chapterByOrder, ...sceneByOrder, ...rest].slice(0, limit);
}

export function mergeMentionableEntities(
  existing: MentionableEntity[],
  created: MentionableEntity[],
): MentionableEntity[] {
  const existingIds = new Set(existing.map((entity) => entity.id));
  return [...created.filter((entity) => !existingIds.has(entity.id)), ...existing];
}

function buildCreateItems(
  query: string,
  options?: {
    createableTypes?: CreateableMentionableEntityType[];
    getCreateLabel?: (type: CreateableMentionableEntityType) => string;
  },
): MentionCreateItem[] {
  const createableTypes = options?.createableTypes ?? [];
  const getCreateLabel = options?.getCreateLabel;
  const trimmedQuery = query.trim();
  return createableTypes.map((type) => ({
    id: `mention-create-${type}`,
    label: getCreateLabel ? getCreateLabel(type) : type,
    type,
    suggestionKind: 'create',
    query: trimmedQuery,
  }));
}

export function buildMentionSuggestionItems(
  entities: MentionableEntity[],
  query: string,
  options?: {
    noMatchLabel?: string;
    createableTypes?: CreateableMentionableEntityType[];
    getCreateLabel?: (type: CreateableMentionableEntityType) => string;
    limit?: number;
  },
): MentionSuggestionItem[] {
  const limit = options?.limit ?? MENTION_SUGGESTION_LIMIT;
  const matches = filterMentionableEntities(entities, query, limit);
  const createItems = matches.length < limit ? buildCreateItems(query, options) : [];

  if (matches.length === 0) {
    const noMatchItem: MentionNoMatchItem = {
      id: MENTION_NO_MATCH_ID,
      label: options?.noMatchLabel ?? '',
      type: 'no_match',
      suggestionKind: 'no_match',
    };
    return [noMatchItem, ...createItems];
  }

  return [...matches, ...createItems];
}

type MentionListProps = {
  items: MentionSuggestionItem[];
  command: (item: MentionSuggestionItem) => void;
  clientRect?: (() => DOMRect | null) | null;
};

const SELECTABLE_ITEM_CLASS = 'mention-item';

export function createMentionSuggestionRenderer(
  getTypeLabel: (type: string) => string,
  getCurrentDocumentId?: () => string | null | undefined,
) {
  return () => {
    let list: HTMLElement | undefined;
    let currentItems: MentionSuggestionItem[] = [];
    let currentCommand: (item: MentionSuggestionItem) => void = () => {};

    const positionList = (props: { clientRect?: (() => DOMRect | null) | null }) => {
      const rect = props.clientRect?.();
      if (!rect || !list) return;
      list.style.position = 'fixed';
      list.style.top = `${rect.bottom + 2}px`;
      list.style.left = `${rect.left}px`;
    };

    const selectableItems = (): NodeListOf<Element> =>
      list?.querySelectorAll(`.${SELECTABLE_ITEM_CLASS}`) ?? document.querySelectorAll('.none');

    const updateList = (props: MentionListProps) => {
      currentItems = props.items;
      currentCommand = props.command;
      if (!list) return;
      positionList(props);
      list.innerHTML = '';
      let firstSelectableAssigned = false;
      props.items.forEach((item: MentionSuggestionItem, idx: number) => {
        if (isMentionNoMatchItem(item)) {
          const emptyRow = document.createElement('div');
          emptyRow.className = 'mention-item-empty';
          emptyRow.style.cssText =
            'display:flex;align-items:center;width:100%;padding:8px 12px;text-align:left;font-size:13px;color:var(--muted-foreground);cursor:default;user-select:none;';
          emptyRow.textContent = item.label;
          list?.appendChild(emptyRow);
          return;
        }

        const btn = document.createElement('button');
        btn.type = 'button';
        const isSelected = !firstSelectableAssigned;
        firstSelectableAssigned = true;
        btn.className = isSelected ? `${SELECTABLE_ITEM_CLASS} mention-item-selected` : SELECTABLE_ITEM_CLASS;
        btn.dataset.mentionIndex = String(idx);
        btn.style.cssText =
          'display:flex;align-items:center;gap:8px;width:100%;padding:8px 12px;text-align:left;border:none;background:none;cursor:pointer;font-size:14px;color:inherit;';
        if (isSelected) btn.style.backgroundColor = 'var(--muted)';
        const iconWrap = document.createElement('span');
        iconWrap.style.cssText = 'display:inline-flex;flex-shrink:0;color:currentColor;';
        iconWrap.setAttribute('title', getTypeLabel(item.type));
        iconWrap.innerHTML = getEntityIconSvg(item.type);
        btn.appendChild(iconWrap);
        const labelWrap = document.createElement('span');
        labelWrap.style.cssText = 'display:flex;flex-direction:column;min-width:0;gap:1px;';
        const labelSpan = document.createElement('span');
        labelSpan.textContent = item.label;
        labelWrap.appendChild(labelSpan);
        const documentTitle =
          'documentTitle' in item && typeof item.documentTitle === 'string'
            ? item.documentTitle.trim()
            : '';
        if (
          documentTitle &&
          shouldShowMentionDocumentTitle(
            { label: item.label, documentId: 'documentId' in item ? item.documentId : undefined, documentTitle },
            getCurrentDocumentId?.() ?? null,
          )
        ) {
          const docSpan = document.createElement('span');
          docSpan.style.cssText = 'font-size:11px;opacity:0.7;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
          docSpan.textContent = documentTitle;
          labelWrap.appendChild(docSpan);
        }
        btn.appendChild(labelWrap);
        btn.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          props.command(item);
        });
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          props.command(item);
        });
        btn.addEventListener('mouseenter', () => {
          list?.querySelectorAll(`.${SELECTABLE_ITEM_CLASS}`).forEach((el) => {
            el.classList.remove('mention-item-selected');
            (el as HTMLElement).style.backgroundColor = '';
          });
          btn.classList.add('mention-item-selected');
          btn.style.backgroundColor = 'var(--muted)';
        });
        list?.appendChild(btn);
      });
    };

    return {
      onStart: (props: MentionListProps) => {
        list = document.createElement('div');
        list.className = 'mention-list';
        list.setAttribute('data-mention-list', '');
        list.style.cssText =
          'position:fixed;z-index:99999;background:var(--popover);color:var(--popover-foreground);border:1px solid var(--border);border-radius:6px;box-shadow:0 4px 6px -1px rgb(0 0 0/0.1);max-height:200px;overflow-y:auto;min-width:180px;cursor:pointer;pointer-events:auto;';
        list.addEventListener(
          'wheel',
          (e) => {
            if (!list) return;
            const { scrollTop, scrollHeight, clientHeight } = list;
            const isScrollable = scrollHeight > clientHeight;
            const isAtTop = scrollTop <= 0 && e.deltaY < 0;
            const isAtBottom = scrollTop >= scrollHeight - clientHeight && e.deltaY > 0;
            if (isScrollable && !isAtTop && !isAtBottom) {
              e.preventDefault();
              list.scrollTop += e.deltaY;
            }
          },
          { passive: false },
        );
        document.body.appendChild(list);
        updateList(props);
      },
      onUpdate: (props: MentionListProps) => {
        updateList(props);
      },
      onKeyDown(props: { event: KeyboardEvent }) {
        if (props.event.key === 'Escape') {
          props.event.preventDefault();
          props.event.stopPropagation();
          list?.remove();
          return true;
        }
        if (!list) return false;
        const selected = list.querySelector('.mention-item-selected');
        const itemElements = selectableItems();
        const scrollSelectedIntoView = (el: Element) => {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        };
        const setSelected = (el: Element | null) => {
          if (!el || !list) return;
          list.querySelectorAll(`.${SELECTABLE_ITEM_CLASS}`).forEach((e) => {
            e.classList.remove('mention-item-selected');
            (e as HTMLElement).style.backgroundColor = '';
          });
          el.classList.add('mention-item-selected');
          (el as HTMLElement).style.backgroundColor = 'var(--muted)';
          scrollSelectedIntoView(el);
        };
        if (props.event.key === 'ArrowDown') {
          props.event.preventDefault();
          const nextEl = !selected
            ? itemElements[0]
            : selected.nextElementSibling?.classList.contains(SELECTABLE_ITEM_CLASS)
              ? selected.nextElementSibling
              : itemElements[0];
          setSelected(nextEl ?? null);
          return true;
        }
        if (props.event.key === 'ArrowUp') {
          props.event.preventDefault();
          const prevEl = !selected
            ? itemElements[itemElements.length - 1]
            : selected.previousElementSibling?.classList.contains(SELECTABLE_ITEM_CLASS)
              ? selected.previousElementSibling
              : itemElements[itemElements.length - 1];
          setSelected(prevEl ?? null);
          return true;
        }
        if (props.event.key === 'Enter' && selected) {
          props.event.preventDefault();
          const idx = Number((selected as HTMLElement).dataset.mentionIndex);
          const item = Number.isInteger(idx) ? currentItems[idx] : undefined;
          if (item && !isMentionNoMatchItem(item)) currentCommand(item);
          return true;
        }
        return false;
      },
      onExit() {
        list?.remove();
      },
    };
  };
}
